/**
 * Browser-side staff push: support checks, permission, and the service
 * worker subscription. Storage is in api/pushNotifications.ts.
 *
 * iPhone rules that shape this file: Safari delivers web push only to a site
 * added to the Home Screen, and the permission prompt only appears when
 * requested inside a tap. `subscribeToPushNotifications` is therefore called
 * from the button's own handler and refuses on an uninstalled iPhone with a
 * message that says what to do, rather than failing quietly.
 */

import { saveStaffSubscription, deactivateStaffSubscription } from '../api/pushNotifications'

export const VAPID_PUBLIC_KEY = (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) || ''
export const PUSH_NOT_CONFIGURED =
  'Push is not configured on this deployment (VITE_VAPID_PUBLIC_KEY is unset).'
export const IOS_INSTALL_FIRST =
  'On iPhone and iPad, push only works once Reppr is on your Home Screen. In Safari, tap Share, then "Add to Home Screen", and open Reppr from there.'

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export function isIOS(): boolean {
  const ua = navigator.userAgent
  return /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true
}

/** iPhone or iPad in a plain Safari tab: push cannot be delivered here. */
export function needsHomeScreenInstall(): boolean {
  return isIOS() && !isStandalone()
}

export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) return 'denied'
  return Notification.permission
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null
  const registration = await navigator.serviceWorker.ready
  return registration.pushManager.getSubscription()
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const output = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i)
  return output
}

/**
 * A VAPID public key is an uncompressed P-256 point: 65 bytes, first byte
 * 0x04, 87 characters in base64url. A pasted value missing one character
 * decodes to 64 bytes and the browser only says "not valid"; this says
 * what is wrong and where to fix it.
 */
export function vapidKeyProblem(key: string): string | null {
  if (!key) return PUSH_NOT_CONFIGURED
  let bytes: Uint8Array
  try {
    bytes = urlBase64ToUint8Array(key)
  } catch {
    return `VITE_VAPID_PUBLIC_KEY is not base64url (${key.length} characters). Check the value in Vercel and redeploy.`
  }
  if (bytes.length !== 65 || bytes[0] !== 4) {
    return `VITE_VAPID_PUBLIC_KEY is not a valid key (${key.length} characters, expected 87). Check the value in Vercel and redeploy.`
  }
  return null
}

export function applicationServerKey(): ArrayBuffer {
  const problem = vapidKeyProblem(VAPID_PUBLIC_KEY)
  if (problem) throw new Error(problem)
  return urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer
}

/** True when the browser's subscription was created with our VAPID key. */
export function subscriptionKeyMatches(sub: PushSubscription): boolean {
  const key = sub.options?.applicationServerKey
  if (!key) return true
  const ours = urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
  const theirs = new Uint8Array(key)
  if (ours.length !== theirs.length) return false
  for (let i = 0; i < ours.length; i++) if (ours[i] !== theirs[i]) return false
  return true
}

/**
 * Enable push on this device and register it. Must run inside a user tap.
 * Throws with a message the settings card can show as-is.
 */
export async function subscribeToPushNotifications(): Promise<void> {
  if (!isPushSupported()) throw new Error('This browser does not support push notifications.')
  if (!VAPID_PUBLIC_KEY) throw new Error(PUSH_NOT_CONFIGURED)
  if (needsHomeScreenInstall()) throw new Error(IOS_INSTALL_FIRST)

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error('Notification permission was not granted.')

  const registration = await navigator.serviceWorker.ready
  let subscription = await registration.pushManager.getSubscription()
  if (subscription && !subscriptionKeyMatches(subscription)) {
    // Subscribed under an older key; the server could never reach it.
    await subscription.unsubscribe()
    subscription = null
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(),
    })
  }
  await saveStaffSubscription(subscription)
}

/** Turn push off on this device: server first, then the browser. */
export async function unsubscribeFromPushNotifications(): Promise<void> {
  const subscription = await getCurrentSubscription()
  if (!subscription) return
  await deactivateStaffSubscription(subscription.endpoint)
  await subscription.unsubscribe()
}
